/**
 * FileUpload — drag-drop / click-to-select file input for assignment submissions.
 * Validates type (PDF/image/audio) and size (≤100MB) client-side before adding.
 * Max 100 files per submission.
 * Calls onFilesChange with the current file list.
 */
import { useRef } from 'react';
import { Upload, X, FileText, Image, Music } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const ALLOWED_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/webp',
  'audio/mpeg',
  'audio/mp4',
  'audio/x-m4a',
];
const MAX_SIZE_BYTES = 100 * 1024 * 1024; // 100MB
const MAX_FILES = 100;

function fileIcon(type: string) {
  if (type.startsWith('image/')) return <Image className="h-4 w-4 text-[var(--color-info)]" />;
  if (type.startsWith('audio/')) return <Music className="h-4 w-4 text-muted-foreground" />;
  return <FileText className="h-4 w-4 text-destructive" />;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

interface FileUploadProps {
  files: File[];
  onFilesChange: (files: File[]) => void;
  disabled?: boolean;
}

export function FileUpload({ files, onFilesChange, disabled }: FileUploadProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  function validateAndAdd(incoming: File[]) {
    const valid: File[] = [];
    for (const file of incoming) {
      if (!ALLOWED_TYPES.includes(file.type)) {
        toast.error(`"${file.name}": Loại file không hỗ trợ (chỉ PDF, ảnh, audio)`);
        continue;
      }
      if (file.size > MAX_SIZE_BYTES) {
        toast.error(`"${file.name}": File vượt 100MB`);
        continue;
      }
      valid.push(file);
    }
    const combined = [...files, ...valid];
    if (combined.length > MAX_FILES) {
      toast.error('Tối đa 100 tệp');
      onFilesChange(combined.slice(0, MAX_FILES));
      return;
    }
    onFilesChange(combined);
  }

  function handleInput(e: React.ChangeEvent<HTMLInputElement>) {
    const picked = Array.from(e.target.files ?? []);
    validateAndAdd(picked);
    // Reset so same file can be re-picked after removal
    e.target.value = '';
  }

  function handleDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    const dropped = Array.from(e.dataTransfer.files);
    validateAndAdd(dropped);
  }

  function removeFile(index: number) {
    const next = files.filter((_, i) => i !== index);
    onFilesChange(next);
  }

  return (
    <div className="space-y-3">
      {/* Drop zone */}
      <div
        onDragOver={(e) => e.preventDefault()}
        onDrop={handleDrop}
        className="border-2 border-dashed border-border rounded-lg p-4 text-center cursor-pointer hover:border-primary/50 transition-colors"
        onClick={() => !disabled && inputRef.current?.click()}
        role="button"
        aria-label="Chọn hoặc kéo-thả file"
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && !disabled && inputRef.current?.click()}
      >
        <Upload className="h-6 w-6 text-muted-foreground mx-auto mb-2" />
        <p className="text-sm text-muted-foreground">
          Kéo-thả hoặc{' '}
          <span className="text-primary font-medium">chọn file</span>
        </p>
        <p className="text-xs text-muted-foreground mt-1">
          PDF, ảnh (JPG/PNG/WebP), audio (MP3/M4A) · Tối đa 100MB/file · {MAX_FILES} file/bài
        </p>
        <input
          ref={inputRef}
          type="file"
          multiple
          accept=".pdf,.jpg,.jpeg,.png,.webp,.mp3,.m4a,application/pdf,image/*,audio/*"
          className="hidden"
          onChange={handleInput}
          disabled={disabled}
        />
      </div>

      {/* File chips */}
      {files.length > 0 && (
        <div className="space-y-1.5">
          {files.map((file, i) => (
            <div
              key={i}
              className="flex items-center gap-2 border border-border rounded-md px-3 py-2 bg-muted/30"
            >
              {fileIcon(file.type)}
              <span className="flex-1 text-sm truncate min-w-0">{file.name}</span>
              <span className="text-xs text-muted-foreground shrink-0">{formatBytes(file.size)}</span>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-6 w-6 shrink-0 text-muted-foreground hover:text-destructive"
                onClick={() => removeFile(i)}
                disabled={disabled}
                aria-label={`Xóa ${file.name}`}
              >
                <X className="h-3.5 w-3.5" />
              </Button>
            </div>
          ))}
          <p className="text-xs text-muted-foreground">
            {files.length} tệp · {formatBytes(files.reduce((acc, f) => acc + f.size, 0))} tổng
          </p>
        </div>
      )}
    </div>
  );
}
